import React, {FC, useRef, useState} from 'react';
import {connect} from 'react-redux';

import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {buildCancelTokenSource, CancelTokenSource} from '../../../helpers/asyncCancelHelpers';
import {makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {RuleBundleTypesEnum} from '../../../models/api/ruleCommonTypes';
import {
  ApiSearchSettingsPayload,
  SettingsSearchItemModel,
  SettingsSearchItemTypesEnum,
} from '../../../models/api/settingsSearchModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {SettingsTabsPagesEnum} from '../../../models/url/settingsUrlModel';
import {fetchApiAsync} from '../../../services/http/http';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ResourceMap} from '../../../store/appTypes';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {getUser} from '../../../store/data/teammates/teammatesSelectors';
import {getTeamsByNamespace, getTeamsUserIsAdminOf} from '../../../store/data/teams/teamsSelectors';
import {getRuleParametersDependencies} from '../../../store/settings/settingsRulesSelectors';
import {makeGetSettingsTypeTrackingField} from '../../../store/settings/settingsTrackingSelectors';
import {SettingsHomeNamespaceSelectDialog} from '../../home/settingsHomeNamespaceSelectDialog';
import {
  isNamespaceSelectResourceType,
  makePathFromNamespaceSelectResourceType,
  NamespaceSelectResourceTypesEnum,
} from '../../home/settingsHomeTypes';
import {makeSettingsUrlFromPathId} from '../../menu/settingsHelpers';
import {RuleParametersDependencies} from '../../rules/ruleConditionModel';
import {
  SettingsSearchSeeAllHandler,
  SettingsSearchSelectItemHandler,
  SettingsSearchTrackSearchQueryHandler,
  SettingsSearchTrackSearchResultHandler,
} from './settingsSearchCommonTypes';
import {SettingsTopbarSearch} from './settingsTopbarSearch';

/*
 * Props.
 */

interface SettingsTopbarSearchContainerProps {
  settingsPath: string;
}

/*
 * Redux.
 */

function getLocationPathAndQuery(_state: unknown, props: SettingsTopbarSearchContainerProps) {
  return props.settingsPath;
}

const mapStateToProps = makeMapStateToProps<SettingsTopbarSearchContainerProps>()(() => ({
  teamsMap: getTeamsByNamespace,
  user: getUser,
  companyUrl: getCompanyUrl,
  settingsTypeTrackingField: makeGetSettingsTypeTrackingField(getLocationPathAndQuery),
  teamsUserIsAdminOf: getTeamsUserIsAdminOf,
  ruleParametersDependencies: getRuleParametersDependencies,
}));

interface SettingsTopbarSearchContainerComponentProps {
  teamsMap: ResourceMap<TeamModel>;
  user: TeammateModel;
  companyUrl: string;
  settingsTypeTrackingField: string | undefined;
  teamsUserIsAdminOf: ReadonlyArray<TeamModel>;
  ruleParametersDependencies: RuleParametersDependencies;
}

const SettingsTopbarSearchContainerComponent: FC<SettingsTopbarSearchContainerComponentProps> = (props) => {
  const {navigateToLink} = useLinkNavigation();
  const [namespaceSelectResourceType, setNamespaceSelectResourceType] = useState<
    NamespaceSelectResourceTypesEnum | undefined
  >(undefined);

  const onSeeAllClick: SettingsSearchSeeAllHandler = (resourceType) => {
    const seeAllUrlOrResourceType = makeSeeAllUrl(resourceType, props.user.namespace);

    if (isNamespaceSelectResourceType(seeAllUrlOrResourceType)) {
      setNamespaceSelectResourceType(seeAllUrlOrResourceType);
      return;
    }

    if (!seeAllUrlOrResourceType) {
      return;
    }

    navigateToLink(seeAllUrlOrResourceType);
  };
  const onSelectItem: SettingsSearchSelectItemHandler = (payload, index) => {
    const selectItemUrl = makeSelectItemUrl(payload, index, props.user.namespace, props.teamsUserIsAdminOf);

    if (isNamespaceSelectResourceType(selectItemUrl)) {
      setNamespaceSelectResourceType(selectItemUrl);
      return;
    }

    if (!selectItemUrl) {
      return;
    }

    navigateToLink(selectItemUrl);
  };
  const trackSearchQuery: SettingsSearchTrackSearchQueryHandler = (value) => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_SEARCH_QUERY, {
      ...value,
      search_type: 'global',
      settings_type: props.settingsTypeTrackingField,
    });
  };
  const trackSearchResultClick: SettingsSearchTrackSearchResultHandler = (value) => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_SEARCH_CLICK, {
      ...value,
      search_type: 'global',
      settings_type: props.settingsTypeTrackingField,
    });
  };

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  function maybeCancelRequest() {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  }

  const querySearch = async (query: string) => {
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    return fetchApiAsync(
      ApiSearchSettingsPayload,
      `${props.companyUrl}/search_settings/${encodeURIComponent(query)}`,
      {
        cancelToken: cancelTokenSource.token,
      },
    );
  };

  const maybeRenderDialog = () => {
    if (namespaceSelectResourceType) {
      return (
        <SettingsHomeNamespaceSelectDialog
          onRequestClose={() => {
            setNamespaceSelectResourceType(undefined);
          }}
          isCreatingResource={false}
          shouldShowOnlyTeams={false}
          resourceType={namespaceSelectResourceType}
          onTeamSelect={(namespace) => {
            const newPath = makePathFromNamespaceSelectResourceType(
              namespace,
              false,
              namespaceSelectResourceType,
            );
            const newUrl = makeSettingsUrlFromPathId(newPath, {namespace});
            navigateToLink(newUrl);
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {maybeRenderDialog()}
      <SettingsTopbarSearch
        user={props.user}
        teamsMap={props.teamsMap}
        onSeeAllClick={onSeeAllClick}
        onSelectItem={onSelectItem}
        trackSearchQuery={trackSearchQuery}
        trackSearchResultClick={trackSearchResultClick}
        makeSettingsSearch={querySearch}
        ruleParametersDependencies={props.ruleParametersDependencies}
      />
    </>
  );
};

export const SettingsTopbarSearchContainer = connect(mapStateToProps)(SettingsTopbarSearchContainerComponent);

/*
 * Helpers.
 */

function makeSeeAllUrl(
  resourceType: SettingsSearchItemTypesEnum,
  userNamespace: string,
): NamespaceSelectResourceTypesEnum | string | undefined {
  const resourceTypeToResourceEnum = {
    [SettingsSearchItemTypesEnum.ANSWER]: NamespaceSelectResourceTypesEnum.ANSWERS,
    [SettingsSearchItemTypesEnum.INBOX]: NamespaceSelectResourceTypesEnum.INBOXES,
    [SettingsSearchItemTypesEnum.ROLE]: NamespaceSelectResourceTypesEnum.ROLES,
    [SettingsSearchItemTypesEnum.RULE]: NamespaceSelectResourceTypesEnum.RULES,
    [SettingsSearchItemTypesEnum.SIGNATURE]: NamespaceSelectResourceTypesEnum.SIGNATURES,
    [SettingsSearchItemTypesEnum.TAG]: NamespaceSelectResourceTypesEnum.TAGS,
    [SettingsSearchItemTypesEnum.CUSTOM_VIEW]: NamespaceSelectResourceTypesEnum.CUSTOM_VIEWS,
  };

  switch (resourceType) {
    case SettingsSearchItemTypesEnum.ANSWER:
    case SettingsSearchItemTypesEnum.CUSTOM_VIEW:
    case SettingsSearchItemTypesEnum.INBOX:
    case SettingsSearchItemTypesEnum.ROLE:
    case SettingsSearchItemTypesEnum.SIGNATURE:
    case SettingsSearchItemTypesEnum.RULE:
    case SettingsSearchItemTypesEnum.TAG:
      return resourceTypeToResourceEnum[resourceType];

    case SettingsSearchItemTypesEnum.INTEGRATION:
      return makeSettingsUrlFromPathId('settings.integrations.native.list');
    case SettingsSearchItemTypesEnum.IMPORT_ROUTE:
      return makeSettingsUrlFromPathId('settings.tools.imports.list');
    case SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE:
      return makeSettingsUrlFromPathId('settings.myPreferences', {
        namespace: userNamespace,
      });
    case SettingsSearchItemTypesEnum.COMPANY_PREFERENCE:
      return makeSettingsUrlFromPathId('settings.companyPreferences.general', {}, {search: ''});
    case SettingsSearchItemTypesEnum.TEAM:
      return makeSettingsUrlFromPathId('settings.teams');
    case SettingsSearchItemTypesEnum.TEAMMATE:
      return makeSettingsUrlFromPathId('settings.teammates', {namespace: 'global'});
    case SettingsSearchItemTypesEnum.PLUGIN:
      return makeSettingsUrlFromPathId('settings.tools.plugins.list');
    case SettingsSearchItemTypesEnum.VACATION_RESPONDER:
      return makeSettingsUrlFromPathId('settings.responder.list', {namespace: userNamespace});
    default:
      return undefined;
  }
}

function makeSelectItemUrl(
  payloadItem: SettingsSearchItemModel,
  itemIndex: number,
  userNamespace: string,
  teamsUserIsAdminOf: ReadonlyArray<TeamModel>,
): NamespaceSelectResourceTypesEnum | string | undefined {
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.INTEGRATION) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.integrations.native.edit.details', {integration: item.type});
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.INBOX) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.inboxes.edit.overview', {
      namespace: item.inbox.namespace,
      inbox: item.inbox.id,
    });
  }

  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.CUSTOM_VIEW) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.custom-views.edit', {
      namespace: item.namespace,
      customView: item.id,
    });
  }

  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.ROLE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.roles.edit.overview', {
      namespace: item.role.namespace,
      role: item.role.alias,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.RULE) {
    const item = payloadItem.items[itemIndex];
    if (item.ruleBundleType === RuleBundleTypesEnum.SLA) {
      return makeSettingsUrlFromPathId('settings.rules.recipes', {
        namespace: item.rule.namespace,
        recipeid: String(item.ruleBundleId),
      });
    }

    if (item.ruleBundleType === RuleBundleTypesEnum.CSAT) {
      return makeSettingsUrlFromPathId('settings.rules.recipecsat', {
        namespace: item.rule.namespace,
        recipeid: String(item.ruleBundleId),
      });
    }

    return makeSettingsUrlFromPathId('settings.rules.ui.edit', {
      namespace: item.rule.namespace,
      ruleid: item.rule.id,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TEAM) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.teams.edit', {team: item.team.id});
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.SIGNATURE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.signatures.edit.overview', {
      signature: item.id,
      namespace: item.namespace,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TEAMMATE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.teammates.edit.overview', {
      teammate: item.alias,
      namespace: 'global',
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TAG) {
    const item = payloadItem.items[itemIndex];
    if (item.tag.archivedAt) {
      return makeSettingsUrlFromPathId(
        'settings.new-tags',
        {
          namespace: item.tag.namespace,
        },
        {
          page: SettingsTabsPagesEnum.ARCHIVED,
          search: item.tag.name,
        },
      );
    }

    return makeSettingsUrlFromPathId('settings.new-tags.edit', {
      tag: item.tag.id,
      namespace: item.tag.namespace,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.IMPORT_ROUTE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.tools.imports.new', {
      importType: item.key.replace('Import', '').toLowerCase(),
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE) {
    const item = payloadItem.items[itemIndex];
    if (item.key === 'singleSignOn') {
      return makeSettingsUrlFromPathId('settings.companyPreferences.sso', {});
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.COMPANY_PREFERENCE) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.companyPreferences.general', {}, {search: item.title});
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE) {
    const item = payloadItem.items[itemIndex];
    switch (item.key) {
      case 'billing': {
        return makeSettingsUrlFromPathId('settings.billing', {});
      }
      case 'deliverability': {
        return makeSettingsUrlFromPathId('settings.deliverability', {});
      }
      case 'loadBalancing': {
        if (teamsUserIsAdminOf.length > 1) {
          return NamespaceSelectResourceTypesEnum.LOAD_BALANCING;
        }
        return makeSettingsUrlFromPathId('settings.rules.ui.limits.list', {
          namespace: teamsUserIsAdminOf[0].namespace,
        });
      }
      default: {
        return undefined;
      }
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE) {
    const item = payloadItem.items[itemIndex];
    if (item.key === 'customSnoozes') {
      return makeSettingsUrlFromPathId('settings.snoozes.list', {namespace: userNamespace});
    }

    return makeSettingsUrlFromPathId(
      'settings.myPreferences',
      {
        namespace: userNamespace,
      },
      {
        search: item.title,
      },
    );
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.SETTINGS_ROUTE) {
    const item = payloadItem.items[itemIndex];

    switch (item.key) {
      case 'myProfile': {
        return makeSettingsUrlFromPathId('settings.profile.overview', {});
      }
      case 'notifications': {
        return makeSettingsUrlFromPathId('settings.notifications', {});
      }
      default: {
        return undefined;
      }
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.TEAMMATE_ROUTE) {
    const item = payloadItem.items[itemIndex];
    if (item.key === 'activityExport') {
      if (teamsUserIsAdminOf.length > 1) {
        return NamespaceSelectResourceTypesEnum.ACTIVITY_EXPORT;
      }
      return makeSettingsUrlFromPathId('settings.teammates.export', {
        namespace: teamsUserIsAdminOf[0].namespace,
      });
    }
    if (item.key === 'userProvisioning') {
      if (teamsUserIsAdminOf.length > 1) {
        return NamespaceSelectResourceTypesEnum.USER_PROVISIONING;
      }
      return makeSettingsUrlFromPathId('settings.teammates.provisioning', {
        namespace: teamsUserIsAdminOf[0].namespace,
      });
    }
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.PLUGIN) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.tools.plugins.edit', {plugin: item.id});
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.CHANNEL) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.inboxes.edit.channels.channel', {
      namespace: item.namespace,
      inbox: item.inboxId,
      channelId: item.id,
    });
  }
  if (payloadItem.resourceType === SettingsSearchItemTypesEnum.DELEGATION) {
    const item = payloadItem.items[itemIndex];
    return makeSettingsUrlFromPathId('settings.inboxes.delegations.edit', {
      namespace: userNamespace,
      delegation: item.delegation.id,
    });
  }

  return undefined;
}
